import React from "react";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import debounce from "debounce-promise";

import apiClient from "../../../../application/apis/api-client";
import {
  isNotFoundError,
  isPermissionError,
  reportProcManagementRelatedError,
} from "../../../../application/domain/proc-management/util/error";
import type { PlaceForProcValidation } from "../../../../application/domain/proc-management";
import { getMaxLength } from "../../../../application/domain/proc-management/util/input-max-length";
import { FormFieldLabel } from "../../../atoms/proc-management/form";
import { ServiceProviderR } from "../../../pages/service-provider/routes";
import { ProcManagementR } from "../../../pages/proc-management/route";
import { InputNoticeList } from "./input-notice-list";
import { ListInput } from "./list-input";
import { SimpleTextInput } from "./simple-text-input";
import { InputProps, PathProps } from "./types";

type Props = {
  id: string;
  name: string;
  counterName?: string;
  usedIf?: string[];
  orgId: string;
  isServiceProviderAccount?: boolean;
  validationErrors?: PlaceForProcValidation;
} & InputProps &
  PathProps;

type OptionType = {
  label: string;
  value: string;
};

export const PlaceInputForProc: React.FC<Props> = (props) => {
  const {
    id,
    name,
    counterName,
    onChange,
    basePath,
    isServiceProviderAccount = false,
    orgId,
    disabled = false,
    shouldDisplayError = false,
    validationErrors,
  } = props;
  const usedIf = props.usedIf ?? [];
  const usedIfvalidationErrors = validationErrors?.usedIf ?? [];
  const linkForPlaceList = isServiceProviderAccount
    ? ServiceProviderR.ProcManagementPlacesList.link({ orgId })
    : ProcManagementR.PlaceList.link({});
  const isErrorForPlace = validationErrors?.id && validationErrors?.id !== "";

  const currentPlace: OptionType = { label: name, value: id };
  const loadOptions = debounce(async (input: string) => {
    try {
      const { data: places } = await apiClient.procManagement.listPlaces({
        organizationId: orgId,
        query: input,
      });
      return places.items.map((p) => {
        const opt: OptionType = {
          label: p.name,
          value: p.id,
        };
        return opt;
      });
    } catch (e) {
      reportProcManagementRelatedError(e);
      if (isNotFoundError(e)) {
        toast.error(`場所データを見つけることができませんでした。`);
      } else if (isPermissionError(e)) {
        toast.error(`場所データを表示する権限がありません。`);
      } else {
        toast.error(
          `場所データの取得に失敗しました。電波状況のよい場所でリロードしてください。`,
        );
      }
      return [];
    }
  }, 500);
  return (
    <div>
      <div className="mb-2">
        <FormFieldLabel label="場所" bold={false} required />
        <InputNoticeList
          notices={[
            <>
              「場所」データの追加、編集は、
              <a
                href={linkForPlaceList}
                target="_blank"
                rel="noopener noreferrer"
              >
                場所マスタ
              </a>
              から行ってください。
            </>,
          ]}
        />
        <AsyncSelect
          placeholder={"選択してください"}
          value={currentPlace}
          onChange={(opt) => {
            if (opt && opt.value) {
              onChange([...basePath, "id"], opt.value);
              onChange([...basePath, "name"], opt.label);
            }
          }}
          defaultOptions
          loadOptions={loadOptions}
          isDisabled={disabled}
        />
        {isErrorForPlace && shouldDisplayError && (
          <p className="help has-text-weight-normal is-danger">
            {validationErrors?.id}
          </p>
        )}
      </div>

      <div className="mb-2">
        <FormFieldLabel label="詳細窓口名" bold={false} />
        <InputNoticeList
          notices={[
            "特定の局や課、係の窓口など、場所マスタのデータで表現されるよりも詳しい情報を追記できます。",
          ]}
        />
        <SimpleTextInput
          onChange={onChange}
          basePath={[...basePath, "counterName"]}
          value={counterName}
          disabled={disabled}
          shouldDisplayError={shouldDisplayError}
          errorMsg={validationErrors?.counterName}
          maxLength={getMaxLength("placeCounterName")}
        />
      </div>

      <ListInput
        label="利用条件"
        boldLabel={false}
        mb={2}
        component={SimpleTextInput}
        componentProps={usedIf.map((value, i) => ({
          value,
          onChange,
          shouldDisplayError,
          errorMsg: usedIfvalidationErrors[i],
        }))}
        basePath={[...basePath, "usedIf"]}
        onPush={() => {
          onChange([...basePath, "usedIf"], [...usedIf, ""]);
        }}
        onRemove={(i) => {
          onChange(
            [...basePath, "usedIf"],
            [...usedIf.slice(0, i), ...usedIf.slice(i + 1)],
          );
        }}
        disabled={disabled}
        notices={[
          "特定のケースでのみ手続きの処理を受け付ける窓口がある場合、そのための条件を指定することができます。",
          "例: 借地人、借家人が手続きを行う場合",
        ]}
      />
    </div>
  );
};
